import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import { DownloadOutlined, MailOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toPng } from 'html-to-image';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import salesMisReportService from '../../services/salesMisReportService';
import RmRevenueSplitPngCard from './_blocks/RmRevenueSplitPngCard';
import TeamRevenueSplitPngCard from './_blocks/TeamRevenueSplitPngCard';

function formatIndianNumber(value) {
  const num = Number(value);
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number.isFinite(num) ? num : 0);
}

function formatPct(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0%';
  return `${num.toFixed(1)}%`;
}

function formatRevenue(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '₹0';
  if (Math.abs(num) >= 1_00_00_000) {
    return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(num / 1_00_00_000)} Cr`;
  }
  if (Math.abs(num) >= 1_00_000) {
    return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(num / 1_00_000)} L`;
  }
  return `₹${formatIndianNumber(num)}`;
}

const summaryTeamRows = (teamSummary) => {
  if (!teamSummary) return [];
  return [
    { key: 'central', team: 'Central Team', ...teamSummary.central_team },
    { key: 'b2c', team: 'Direct Investors (B2C)', ...teamSummary.direct_investors_b2c },
    { key: 'bd', team: 'BD Team', ...teamSummary.bd_team },
    { key: 'total', team: 'Total', ...teamSummary.total, isTotal: true },
  ];
};

export default function SalesMisReportPage() {
  const [reportDate, setReportDate] = useState(dayjs());
  const [report, setReport] = useState(null);
  const [htmlPreview, setHtmlPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [recipients, setRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPng, setDownloadingPng] = useState(false);
  const [downloadingTeamPng, setDownloadingTeamPng] = useState(false);
  const [form] = Form.useForm();
  const revenueSplitCardRef = useRef(null);
  const teamSplitCardRef = useRef(null);

  const loadRecipients = useCallback(async () => {
    setRecipientsLoading(true);
    try {
      const list = await salesMisReportService.listRecipients();
      setRecipients(list);
    } catch (e) {
      message.error(e?.message || 'Failed to load recipients');
      setRecipients([]);
    } finally {
      setRecipientsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  const filteredRecipients = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(
      (r) =>
        String(r.email || '').toLowerCase().includes(q)
        || String(r.name || '').toLowerCase().includes(q),
    );
  }, [recipients, recipientSearch]);

  const loadReportData = async (dateStr) => {
    const previewResult = await salesMisReportService.previewHtml(dateStr);
    const nextReport = previewResult?.report ?? null;
    setReport(nextReport);
    setHtmlPreview(previewResult?.html ?? '');
    return nextReport;
  };

  const loadPreview = async () => {
    setLoading(true);
    try {
      const dateStr = reportDate.format('YYYY-MM-DD');
      await loadReportData(dateStr);
      setPreviewOpen(true);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadRevenueSplitPng = async () => {
    setDownloadingPng(true);
    try {
      const dateStr = reportDate.format('YYYY-MM-DD');
      let currentReport = report;
      if (!currentReport?.rm_revenue_split?.length) {
        currentReport = await loadReportData(dateStr);
      }
      if (!currentReport?.rm_revenue_split?.length) {
        message.warning('No SALES RM revenue data for this period');
        return;
      }

      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      if (!revenueSplitCardRef.current) {
        message.error('Could not render PNG card');
        return;
      }

      const dataUrl = await toPng(revenueSplitCardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
      });

      const link = document.createElement('a');
      link.download = `sales-rm-revenue-${dateStr}.png`;
      link.href = dataUrl;
      link.click();
      message.success('PNG downloaded');
    } catch (e) {
      message.error(e?.message || 'Failed to download PNG');
    } finally {
      setDownloadingPng(false);
    }
  };

  const handleDownloadTeamSplitPng = async () => {
    setDownloadingTeamPng(true);
    try {
      const dateStr = reportDate.format('YYYY-MM-DD');
      let currentReport = report;
      if (!currentReport?.team_revenue_split?.length) {
        currentReport = await loadReportData(dateStr);
      }
      if (!currentReport?.team_revenue_split?.length) {
        message.warning('No fulfilled revenue for this period');
        return;
      }

      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      if (!teamSplitCardRef.current) {
        message.error('Could not render PNG card');
        return;
      }

      const dataUrl = await toPng(teamSplitCardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
      });

      const link = document.createElement('a');
      link.download = `sales-team-revenue-split-${dateStr}.png`;
      link.href = dataUrl;
      link.click();
      message.success('PNG downloaded');
    } catch (e) {
      message.error(e?.message || 'Failed to download PNG');
    } finally {
      setDownloadingTeamPng(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const dateStr = reportDate.format('YYYY-MM-DD');
      const result = await salesMisReportService.sendReport({
        reportDate: dateStr,
        emailTo: 'CRON',
      });
      const email = result?.email;
      if (email?.skipped) {
        message.warning('No active recipients configured');
        return;
      }
      message.success(`Report sent to ${email?.sent ?? 0} recipient(s)`);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const openCreateRecipient = () => {
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setDrawerOpen(true);
  };

  const onCreateRecipient = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const res = await salesMisReportService.createRecipient({
        email: values.email.trim(),
        name: values.name?.trim() || null,
        is_active: values.is_active,
      });
      if (res?.success === false) {
        message.error(res?.message || 'Create failed');
        return;
      }
      message.success('Recipient added');
      setDrawerOpen(false);
      loadRecipients();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleRecipient = async (record) => {
    try {
      const res = await salesMisReportService.updateRecipient(record.id, {
        is_active: !record.is_active,
      });
      if (res?.success === false) {
        message.error(res?.message || 'Update failed');
        return;
      }
      loadRecipients();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Update failed');
    }
  };

  const deleteRecipient = async (record) => {
    try {
      const res = await salesMisReportService.removeRecipient(record.id);
      if (res?.success === false) {
        message.error(res?.message || 'Delete failed');
        return;
      }
      message.success('Recipient removed');
      loadRecipients();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const revenueColumns = [
    { title: 'Orders', dataIndex: 'total_orders', key: 'total_orders', render: formatIndianNumber },
    { title: 'Txn Value', dataIndex: 'total_transaction_value', key: 'total_transaction_value', render: formatRevenue },
    { title: 'Gross Rev', dataIndex: 'total_gross_revenue', key: 'total_gross_revenue', render: formatRevenue },
    { title: 'Altcase Revenue', dataIndex: 'total_net_revenue', key: 'total_net_revenue', render: formatRevenue },
  ];

  const summaryColumns = [
    { title: 'Team', dataIndex: 'team', key: 'team', fixed: 'left', width: 200 },
    { title: 'Orders', dataIndex: 'total_orders', key: 'total_orders', render: formatIndianNumber },
    { title: 'Txn Value', dataIndex: 'total_transaction_value', key: 'total_transaction_value', render: formatRevenue },
    { title: 'Gross Rev', dataIndex: 'total_gross_revenue', key: 'total_gross_revenue', render: formatRevenue },
    { title: 'Altcase Revenue', dataIndex: 'total_net_revenue', key: 'total_net_revenue', render: formatRevenue },
  ];

  const rmMtdColumns = [
    { title: 'RM Name', dataIndex: 'rm_name', key: 'rm_name', fixed: 'left', width: 180 },
    ...revenueColumns,
  ];

  const processingColumns = [
    { title: 'RM Name', dataIndex: 'rm_name', key: 'rm_name', fixed: 'left', width: 180 },
    { title: 'Orders', dataIndex: 'total_processing_orders', key: 'total_processing_orders', render: formatIndianNumber },
    { title: 'Qty', dataIndex: 'total_quantity', key: 'total_quantity', render: formatIndianNumber },
    { title: 'Txn Value', dataIndex: 'total_transaction_value', key: 'total_transaction_value', render: formatRevenue },
  ];

  const ifaColumns = [
    { title: 'RM Name', dataIndex: 'rm_name', key: 'rm_name', fixed: 'left', width: 180 },
    { title: 'Onboarded', dataIndex: 'total_onboarded', key: 'total_onboarded', render: formatIndianNumber },
    { title: 'Active (Ever)', dataIndex: 'active_overall', key: 'active_overall', render: formatIndianNumber },
    { title: 'MTD Onboard', dataIndex: 'onboarded_this_month', key: 'onboarded_this_month', render: formatIndianNumber },
    { title: 'MTD Active', dataIndex: 'active_this_month', key: 'active_this_month', render: formatIndianNumber },
  ];

  const revenueSplitColumns = [
    { title: 'RM Name', dataIndex: 'rm_name', key: 'rm_name', fixed: 'left', width: 160 },
    { title: 'RM Net Rev', dataIndex: 'net_revenue', key: 'net_revenue', render: formatRevenue },
    { title: 'Partner Rev', dataIndex: 'partner_revenue', key: 'partner_revenue', render: formatRevenue },
    { title: 'Net %', dataIndex: 'net_pct', key: 'net_pct', render: formatPct },
    { title: 'Partner %', dataIndex: 'partner_pct', key: 'partner_pct', render: formatPct },
  ];

  const teamRevenueSplitColumns = [
    { title: 'Team', dataIndex: 'team_name', key: 'team_name', fixed: 'left', width: 220 },
    { title: 'Altcase Revenue', dataIndex: 'net_revenue', key: 'net_revenue', render: formatRevenue },
    { title: '% Share', dataIndex: 'revenue_pct', key: 'revenue_pct', render: formatPct },
  ];

  const recipientColumns = [
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Switch checked={record.is_active} onChange={() => toggleRecipient(record)} size="small" />
          <Popconfirm title="Remove this recipient?" onConfirm={() => deleteRecipient(record)}>
            <Button type="link" danger size="small">Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales MIS Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            RM-wise MTD fulfilled revenue, processing pipeline, and partner funnel (Super Admin only)
          </p>
        </div>
        <Space wrap className="sm:justify-end">
          <DatePicker value={reportDate} onChange={(v) => v && setReportDate(v)} allowClear={false} />
          <Button icon={<ReloadOutlined />} onClick={loadPreview} loading={loading}>
            Preview
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadRevenueSplitPng}
            loading={downloadingPng}
          >
            Download RM Split PNG
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadTeamSplitPng}
            loading={downloadingTeamPng}
          >
            Download Team Split PNG
          </Button>
          <Button type="primary" icon={<MailOutlined />} onClick={handleSend} loading={sending}>
            Send Now
          </Button>
        </Space>
      </div>

      {report?.rm_revenue_split?.length ? (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: -10000,
            top: 0,
            pointerEvents: 'none',
          }}
        >
          <div ref={revenueSplitCardRef}>
            <RmRevenueSplitPngCard
              reportDate={reportDate.format('YYYY-MM-DD')}
              rows={report.rm_revenue_split}
            />
          </div>
        </div>
      ) : null}

      {report?.team_revenue_split?.length ? (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: -10000,
            top: 0,
            pointerEvents: 'none',
          }}
        >
          <div ref={teamSplitCardRef}>
            <TeamRevenueSplitPngCard
              reportDate={reportDate.format('YYYY-MM-DD')}
              rows={report.team_revenue_split}
            />
          </div>
        </div>
      ) : null}

      {report && (
        <div className="space-y-6 mb-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-lg font-semibold text-gray-900 m-0">RM Revenue Split (MTD)</h2>
              <p className="text-sm text-gray-500 mt-1 mb-0">
                SALES RMs by rm_id — RM Net Rev matches CBO dashboard; Partner Rev = fulfilled price − price_partner
              </p>
            </div>
            <Table
              rowKey="rm_name"
              columns={revenueSplitColumns}
              dataSource={report.rm_revenue_split ?? []}
              pagination={false}
              scroll={{ x: 900 }}
              size="small"
              rowClassName={(record) => (record.is_total ? 'font-semibold bg-slate-50' : '')}
            />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-lg font-semibold text-gray-900 m-0">Team Revenue Split (MTD)</h2>
              <p className="text-sm text-gray-500 mt-1 mb-0">
                Fulfilled revenue share — Central Team and B2C use platform margin; BD Team uses RM net
              </p>
            </div>
            <Table
              rowKey="team_name"
              columns={teamRevenueSplitColumns}
              dataSource={report.team_revenue_split ?? []}
              pagination={false}
              scroll={{ x: 600 }}
              size="small"
              rowClassName={(record) => (record.is_total ? 'font-semibold bg-slate-50' : '')}
            />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-lg font-semibold text-gray-900 m-0">Team Summary (MTD)</h2>
            </div>
            <Table
              rowKey="key"
              columns={summaryColumns}
              dataSource={summaryTeamRows(report.team_summary)}
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
              rowClassName={(record) => (record.isTotal ? 'font-semibold bg-slate-50' : '')}
            />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-lg font-semibold text-gray-900 m-0">RM Wise MTD Data</h2>
            </div>
            <Table
              rowKey="rm_name"
              columns={rmMtdColumns}
              dataSource={report.rm_wise_breakdown ?? []}
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
            />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-lg font-semibold text-gray-900 m-0">RM Wise Processing Orders (MTD)</h2>
            </div>
            <Table
              rowKey="rm_name"
              columns={processingColumns}
              dataSource={report.rm_wise_processing_orders ?? []}
              pagination={false}
              scroll={{ x: 700 }}
              size="small"
            />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-lg font-semibold text-gray-900 m-0">Partner Overview (RM Wise)</h2>
            </div>
            <Table
              rowKey="rm_name"
              columns={ifaColumns}
              dataSource={report.rm_wise_ifa_mapping ?? []}
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
            />
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-50/50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 m-0">Email Recipients</h2>
            <p className="text-sm text-gray-500 mt-1 mb-0">
              Recipients for the daily Sales MIS report
            </p>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateRecipient}>
            Add Recipient
          </Button>
        </div>
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
          <Input.Search
            placeholder="Search by email or name..."
            allowClear
            value={recipientSearch}
            onChange={(e) => setRecipientSearch(e.target.value)}
            className="w-full sm:max-w-xs"
          />
          {recipientSearch.trim() && (
            <span className="text-sm text-gray-500">
              Showing {filteredRecipients.length} of {recipients.length}
            </span>
          )}
        </div>
        <Table
          rowKey="id"
          columns={recipientColumns}
          dataSource={filteredRecipients}
          loading={recipientsLoading}
          pagination={false}
          size="middle"
        />
      </div>

      <Modal
        title={`Sales MIS Report — ${reportDate.format('YYYY-MM-DD')}`}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={960}
        destroyOnClose
      >
        {htmlPreview ? (
          <iframe
            title="Sales MIS Report email preview"
            srcDoc={htmlPreview}
            style={{
              width: '100%',
              minHeight: 640,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              background: '#f4f4f4',
            }}
          />
        ) : null}
      </Modal>

      <Drawer
        title="Add Email Recipient"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={400}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onCreateRecipient}>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
          >
            <Input placeholder="recipient@example.com" />
          </Form.Item>
          <Form.Item name="name" label="Name">
            <Input placeholder="Optional display name" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} block>
            Save Recipient
          </Button>
        </Form>
      </Drawer>
    </DashboardPageWrapper>
  );
}
